import React, { useState, useRef, useCallback, useEffect } from 'react';
import { functions } from '../../firebase';
import { httpsCallable } from 'firebase/functions';
import { useToast } from '../ToastContext';
import { fmtTokens } from '../../utils/helpers';

// ─────────────────────────────────────────────────────────────────────────────
// WEB AUDIO — Keno sound effects
// ─────────────────────────────────────────────────────────────────────────────
class KenoAudio {
  constructor() {
    this.ctx = null;
    this.muted = false;
  }

  init() {
    if (!this.ctx) {
      this.ctx = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (this.ctx.state === 'suspended') this.ctx.resume();
  }

  // Tile select/deselect click
  click() {
    if (this.muted || !this.ctx) return;
    const o = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    o.type = 'square';
    o.frequency.value = 600;
    g.gain.setValueAtTime(0.04, this.ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + 0.03);
    o.connect(g).connect(this.ctx.destination);
    o.start(); o.stop(this.ctx.currentTime + 0.03);
  }

  // Draw reveal — hit (ascending chime)
  drawHit(hitCount = 1) {
    if (this.muted || !this.ctx) return;
    const baseFreq = 550 + Math.min(hitCount, 10) * 40;
    const now = this.ctx.currentTime;
    [baseFreq, baseFreq * 1.25].forEach((freq, i) => {
      const o = this.ctx.createOscillator();
      const g = this.ctx.createGain();
      o.type = 'triangle';
      o.frequency.value = freq;
      g.gain.setValueAtTime(0, now + i * 0.06);
      g.gain.linearRampToValueAtTime(0.1, now + i * 0.06 + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, now + i * 0.06 + 0.2);
      o.connect(g).connect(this.ctx.destination);
      o.start(now + i * 0.06);
      o.stop(now + i * 0.06 + 0.2);
    });
  }

  // Draw reveal — miss (soft low note)
  drawMiss() {
    if (this.muted || !this.ctx) return;
    const o = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    o.type = 'sine';
    o.frequency.value = 200;
    g.gain.setValueAtTime(0.03, this.ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + 0.08);
    o.connect(g).connect(this.ctx.destination);
    o.start(); o.stop(this.ctx.currentTime + 0.08);
  }

  // Win jingle
  win() {
    if (this.muted || !this.ctx) return;
    const notes = [523, 659, 784, 1047, 1319];
    const now = this.ctx.currentTime;
    notes.forEach((freq, i) => {
      const o = this.ctx.createOscillator();
      const g = this.ctx.createGain();
      o.type = 'triangle';
      o.frequency.value = freq;
      g.gain.setValueAtTime(0, now + i * 0.1);
      g.gain.linearRampToValueAtTime(0.1, now + i * 0.1 + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, now + i * 0.1 + 0.3);
      o.connect(g).connect(this.ctx.destination);
      o.start(now + i * 0.1); o.stop(now + i * 0.1 + 0.3);
    });
  }

  // Loss sound
  loss() {
    if (this.muted || !this.ctx) return;
    const notes = [330, 262, 220];
    const now = this.ctx.currentTime;
    notes.forEach((freq, i) => {
      const o = this.ctx.createOscillator();
      const g = this.ctx.createGain();
      o.type = 'sine';
      o.frequency.value = freq;
      g.gain.setValueAtTime(0, now + i * 0.15);
      g.gain.linearRampToValueAtTime(0.08, now + i * 0.15 + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, now + i * 0.15 + 0.3);
      o.connect(g).connect(this.ctx.destination);
      o.start(now + i * 0.15); o.stop(now + i * 0.15 + 0.3);
    });
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// PAYOUT TABLES — from the Keno payout sheet
// KENO_PAYOUTS[riskLevel][numPicks][numHits] = multiplier
// Board: 40 tiles, 10 drawn. Player picks 1–10.
// ─────────────────────────────────────────────────────────────────────────────
const KENO_PAYOUTS = {
  classic: {
    1: { 0: 0, 1: 3.96 },
    2: { 0: 0, 1: 1.9, 2: 4.5 },
    3: { 0: 0, 1: 1, 2: 3, 3: 11.5 },
    4: { 0: 0, 1: 0.4, 2: 2.05, 3: 7, 4: 42 },
    5: { 0: 0, 1: 0, 2: 1.7, 3: 4.35, 4: 16.5, 5: 36 },
    6: { 0: 0, 1: 0, 2: 1, 3: 3.68, 4: 7, 5: 16.5, 6: 40 },
    7: { 0: 0, 1: 0, 2: 0.47, 3: 3, 4: 4.5, 5: 14, 6: 31, 7: 60 },
    8: { 0: 0, 1: 0, 2: 0, 3: 2.2, 4: 4, 5: 13, 6: 22, 7: 55, 8: 70 },
    9: { 0: 0, 1: 0, 2: 0, 3: 1.55, 4: 3, 5: 8, 6: 15, 7: 44, 8: 60, 9: 85 },
    10: { 0: 0, 1: 0, 2: 0, 3: 1.4, 4: 2.25, 5: 4.5, 6: 8, 7: 17, 8: 50, 9: 80, 10: 100 },
  },
  low: {
    1: { 0: 0.7, 1: 1.85 },
    2: { 0: 0, 1: 2, 2: 3.8 },
    3: { 0: 0, 1: 1.1, 2: 1.38, 3: 26 },
    4: { 0: 0, 1: 0, 2: 2.2, 3: 7.9, 4: 90 },
    5: { 0: 0, 1: 0, 2: 1.5, 3: 3, 4: 23, 5: 300 },
    6: { 0: 0, 1: 0, 2: 1.1, 3: 2, 4: 5, 5: 105, 6: 1000 },
    7: { 0: 0, 1: 0, 2: 1.1, 3: 1.6, 4: 3, 5: 15, 6: 250, 7: 3000 },
    8: { 0: 0, 1: 0, 2: 1.1, 3: 1.5, 4: 2, 5: 4.5, 6: 32, 7: 500, 8: 5000 },
    9: { 0: 0, 1: 0, 2: 1.1, 3: 1.3, 4: 1.7, 5: 2.5, 6: 7.5, 7: 40, 8: 500, 9: 5000 },
    10: { 0: 0, 1: 0, 2: 1.1, 3: 1.2, 4: 1.3, 5: 1.8, 6: 3.5, 7: 12.5, 8: 50, 9: 500, 10: 5000 },
  },
  medium: {
    1: { 0: 0.4, 1: 2.75 },
    2: { 0: 0, 1: 1.8, 2: 5.1 },
    3: { 0: 0, 1: 0, 2: 2.8, 3: 50 },
    4: { 0: 0, 1: 0, 2: 1.7, 3: 10, 4: 100 },
    5: { 0: 0, 1: 0, 2: 1.3, 3: 4, 4: 20, 5: 310 },
    6: { 0: 0, 1: 0, 2: 0, 3: 2.5, 4: 8, 5: 189, 6: 2000 },
    7: { 0: 0, 1: 0, 2: 0, 3: 2, 4: 6, 5: 27, 6: 500, 7: 5000 },
    8: { 0: 0, 1: 0, 2: 0, 3: 2, 4: 4, 5: 9, 6: 55, 7: 1000, 8: 10000 },
    9: { 0: 0, 1: 0, 2: 0, 3: 2, 4: 2.5, 5: 5, 6: 15, 7: 80, 8: 1000, 9: 10000 },
    10: { 0: 0, 1: 0, 2: 0, 3: 1.6, 4: 2, 5: 4, 6: 7, 7: 25, 8: 100, 9: 1000, 10: 10000 },
  },
  high: {
    1: { 0: 0, 1: 3.95 },
    2: { 0: 0, 1: 0, 2: 17 },
    3: { 0: 0, 1: 0, 2: 0, 3: 81.5 },
    4: { 0: 0, 1: 0, 2: 0, 3: 10, 4: 259 },
    5: { 0: 0, 1: 0, 2: 0, 3: 4, 4: 30, 5: 1000 },
    6: { 0: 0, 1: 0, 2: 0, 3: 0, 4: 10, 5: 270, 6: 4000 },
    7: { 0: 0, 1: 0, 2: 0, 3: 0, 4: 7, 5: 60, 6: 500, 7: 23000 },
    8: { 0: 0, 1: 0, 2: 0, 3: 0, 4: 5, 5: 20, 6: 100, 7: 4300, 8: 45000 },
    9: { 0: 0, 1: 0, 2: 0, 3: 0, 4: 4, 5: 10, 6: 45, 7: 700, 8: 4500, 9: 50000 },
    10: { 0: 0, 1: 0, 2: 0, 3: 0, 4: 3.5, 5: 8, 6: 13, 7: 45, 8: 700, 9: 10000, 10: 100000 },
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────
const ACCENT = '#F59E0B';
const ACCENT_DIM = 'rgba(245,158,11,0.15)';
const ACCENT_BORDER = 'rgba(245,158,11,0.5)';

const BOARD_SIZE = 40;
const DRAW_COUNT = 10;
const RISK_LEVELS = ['classic', 'low', 'medium', 'high'];
const RISK_LABELS = { classic: 'Classic', low: 'Low', medium: 'Medium', high: 'High' };
const PICK_PRESETS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
const REVEAL_INTERVAL_MS = 300;

// ─────────────────────────────────────────────────────────────────────────────
// PAYTABLE MODAL — self-contained with its own risk/picks selectors
// ─────────────────────────────────────────────────────────────────────────────
function KenoPaytableModal({ isMobile, defaultRisk, defaultPicks, onClose }) {
  const [viewRisk, setViewRisk] = useState(defaultRisk);
  const [viewPicks, setViewPicks] = useState(defaultPicks);
  const payouts = KENO_PAYOUTS[viewRisk]?.[viewPicks] || {};

  const btnStyle = (active) => ({
    padding: '5px 10px', borderRadius: '6px', cursor: 'pointer',
    fontSize: '12px', fontWeight: '700',
    background: active ? ACCENT_DIM : 'rgba(255,255,255,0.04)',
    border: `1.5px solid ${active ? ACCENT_BORDER : 'rgba(255,255,255,0.08)'}`,
    color: active ? ACCENT : '#aaa',
    transition: 'all 0.15s',
  });

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.8)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', zIndex: 9999,
        padding: '20px',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#111118', borderRadius: '16px',
          border: `1px solid ${ACCENT_BORDER}`,
          boxShadow: `0 8px 40px rgba(0,0,0,0.5), 0 0 20px rgba(245,158,11,0.1)`,
          maxWidth: '500px', width: '100%',
          maxHeight: isMobile ? '80vh' : '85vh',
          overflow: 'auto', padding: isMobile ? '16px' : '24px',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: ACCENT }}>
            🎱 Keno Paytable
          </h3>
          <button onClick={onClose} style={{
            background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '8px', color: '#aaa', cursor: 'pointer',
            padding: '4px 10px', fontSize: '16px',
          }}>✕</button>
        </div>

        {/* Risk selector */}
        <div style={{ marginBottom: '12px' }}>
          <div style={{ fontSize: '11px', color: '#888', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px', fontWeight: '600' }}>
            Risk
          </div>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {RISK_LEVELS.map(r => (
              <button key={r} onClick={() => setViewRisk(r)} style={btnStyle(viewRisk === r)}>
                {RISK_LABELS[r]}
              </button>
            ))}
          </div>
        </div>

        {/* Picks selector */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '11px', color: '#888', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px', fontWeight: '600' }}>
            Picks
          </div>
          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
            {PICK_PRESETS.map(n => (
              <button key={n} onClick={() => setViewPicks(n)} style={{ ...btnStyle(viewPicks === n), minWidth: '32px' }}>
                {n}
              </button>
            ))}
          </div>
        </div>

        {/* Payout table */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr>
                <th style={{ padding: '8px 12px', textAlign: 'left', color: ACCENT, borderBottom: `2px solid ${ACCENT_BORDER}`, fontWeight: '700' }}>Hits</th>
                <th style={{ padding: '8px 12px', textAlign: 'right', color: ACCENT, borderBottom: `2px solid ${ACCENT_BORDER}`, fontWeight: '700' }}>Multiplier</th>
                <th style={{ padding: '8px 12px', textAlign: 'right', color: '#888', borderBottom: `2px solid ${ACCENT_BORDER}`, fontWeight: '700' }}>Payout (1.00 bet)</th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: viewPicks + 1 }, (_, h) => {
                const mult = payouts[h] ?? 0;
                return (
                  <tr key={h}>
                    <td style={{
                      padding: '6px 12px', borderBottom: '1px solid rgba(255,255,255,0.04)',
                      color: '#ccc', fontWeight: '600',
                    }}>{h} / {viewPicks}</td>
                    <td style={{
                      padding: '6px 12px', textAlign: 'right',
                      borderBottom: '1px solid rgba(255,255,255,0.04)',
                      color: mult >= 100 ? '#FFD700' : mult >= 10 ? ACCENT : mult > 0 ? '#ccc' : '#555',
                      fontWeight: mult >= 10 ? '800' : mult > 0 ? '600' : '400',
                    }}>{mult > 0 ? `${mult.toLocaleString()}x` : '—'}</td>
                    <td style={{
                      padding: '6px 12px', textAlign: 'right',
                      borderBottom: '1px solid rgba(255,255,255,0.04)',
                      color: mult > 0 ? '#aaa' : '#444',
                      fontWeight: '500',
                    }}>{mult > 0 ? mult.toLocaleString() : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* House edge note */}
        <p style={{ fontSize: '11px', color: '#555', marginTop: '12px', marginBottom: 0, textAlign: 'center' }}>
          House edge varies by configuration (~1–2%)
        </p>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
function KenoPage({ user, userData, onUserDataChange, onTokenAdjustmentChange, onBack, isMobile }) {
  const toast = useToast();
  const audioRef = useRef(new KenoAudio());

  // Cloud function ref
  const kenoPlayFn = useRef(httpsCallable(functions, 'kenoPlay')).current;

  // Config state
  const [riskLevel, setRiskLevel] = useState('classic');
  const [numPicks, setNumPicks] = useState(5);
  const [betAmount, setBetAmount] = useState('1');
  const [tokenType, setTokenType] = useState('crewmate');

  // Game state
  const [selectedTiles, setSelectedTiles] = useState(new Set());
  const [drawnTiles, setDrawnTiles] = useState(null); // number[] length 10
  const [revealedCount, setRevealedCount] = useState(0);
  const [hits, setHits] = useState([]);
  const [gamePhase, setGamePhase] = useState('picking'); // 'picking' | 'playing' | 'revealing' | 'result'
  const [resultData, setResultData] = useState(null); // { multiplier, payout }

  // UI state
  const [loading, setLoading] = useState(false);
  const [muted, setMuted] = useState(false);
  const [showPaytable, setShowPaytable] = useState(false);

  // Track hit count during reveal for audio pitch
  const hitCountRef = useRef(0);

  // Mute toggle
  useEffect(() => { audioRef.current.muted = muted; }, [muted]);

  // Reveal animation
  useEffect(() => {
    if (gamePhase !== 'revealing' || !drawnTiles) return;
    hitCountRef.current = 0;
    const timer = setInterval(() => {
      setRevealedCount(prev => {
        const next = prev + 1;
        if (next > DRAW_COUNT) {
          clearInterval(timer);
          return prev;
        }
        // Play sound for this revealed tile
        const tileIdx = drawnTiles[next - 1];
        if (selectedTiles.has(tileIdx)) {
          hitCountRef.current++;
          audioRef.current.drawHit(hitCountRef.current);
        } else {
          audioRef.current.drawMiss();
        }
        return next;
      });
    }, REVEAL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [gamePhase, drawnTiles, selectedTiles]);

  // Transition to result after full reveal
  useEffect(() => {
    if (gamePhase !== 'revealing' || revealedCount < DRAW_COUNT) return;
    const timeout = setTimeout(() => {
      setGamePhase('result');
      if (resultData && resultData.payout > 0) {
        audioRef.current.win();
      } else {
        audioRef.current.loss();
      }
      if (onTokenAdjustmentChange) onTokenAdjustmentChange({ crewmate: 0, imposter: 0, frozen: false });
      if (onUserDataChange) onUserDataChange();
    }, 500);
    return () => clearTimeout(timeout);
  }, [gamePhase, revealedCount, resultData, onTokenAdjustmentChange, onUserDataChange]);

  // Clear picks when numPicks changes
  useEffect(() => {
    setSelectedTiles(new Set());
  }, [numPicks]);

  const balance = tokenType === 'crewmate'
    ? (userData?.crewmateTokens ?? 0)
    : (userData?.imposterTokens ?? 0);

  const isLocked = gamePhase !== 'picking';

  // ─── Handlers ───────────────────────────────────────────────────────────────

  const handleTileSelect = useCallback((index) => {
    if (isLocked) return;
    audioRef.current.init();
    audioRef.current.click();
    setSelectedTiles(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else if (next.size < numPicks) {
        next.add(index);
      }
      return next;
    });
  }, [isLocked, numPicks]);

  const handleAutoPick = useCallback(() => {
    if (isLocked) return;
    audioRef.current.init();
    audioRef.current.click();
    const indices = Array.from({ length: BOARD_SIZE }, (_, i) => i);
    for (let i = indices.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [indices[i], indices[j]] = [indices[j], indices[i]];
    }
    setSelectedTiles(new Set(indices.slice(0, numPicks)));
  }, [isLocked, numPicks]);

  const handleClearPicks = useCallback(() => {
    if (isLocked) return;
    audioRef.current.init();
    audioRef.current.click();
    setSelectedTiles(new Set());
  }, [isLocked]);

  const handlePlay = useCallback(async () => {
    if (selectedTiles.size !== numPicks) { toast.error(`Select exactly ${numPicks} tiles.`); return; }
    const bet = parseFloat(betAmount);
    if (!Number.isFinite(bet) || bet < 0.1) { toast.error('Minimum bet is 0.1 tokens.'); return; }
    if (bet > 10000) { toast.error('Maximum bet is 10,000 tokens.'); return; }
    if (bet > balance) { toast.error('Insufficient balance.'); return; }

    audioRef.current.init();
    audioRef.current.click();
    setLoading(true);
    setGamePhase('playing');

    try {
      if (onTokenAdjustmentChange) {
        onTokenAdjustmentChange({
          crewmate: tokenType === 'crewmate' ? bet : 0,
          imposter: tokenType === 'imposter' ? bet : 0,
          frozen: true,
        });
      }

      const res = await kenoPlayFn({
        betAmount: bet,
        tokenType,
        riskLevel,
        numPicks,
        selectedTiles: [...selectedTiles],
      });
      const data = res.data;

      setDrawnTiles(data.drawnTiles);
      setHits(data.hits);
      setResultData({ multiplier: data.multiplier, payout: data.payout });
      setRevealedCount(0);
      setGamePhase('revealing');
    } catch (err) {
      toast.error(err?.message || 'Failed to play keno.');
      setGamePhase('picking');
      if (onTokenAdjustmentChange) onTokenAdjustmentChange({ crewmate: 0, imposter: 0, frozen: false });
    } finally {
      setLoading(false);
    }
  }, [selectedTiles, numPicks, betAmount, balance, tokenType, riskLevel, kenoPlayFn, toast, onTokenAdjustmentChange]);

  const handleNewGame = useCallback(() => {
    setDrawnTiles(null);
    setRevealedCount(0);
    setHits([]);
    setResultData(null);
    setGamePhase('picking');
  }, []);

  // ─── Tile rendering ────────────────────────────────────────────────────────
  const renderTile = (index) => {
    const num = index + 1;
    const isSelected = selectedTiles.has(index);
    const isDrawn = drawnTiles && revealedCount > 0 && drawnTiles.slice(0, revealedCount).includes(index);
    const isHit = isDrawn && isSelected;
    const isMiss = isDrawn && !isSelected;
    const canClick = gamePhase === 'picking';

    let bg = 'linear-gradient(145deg, #1a1a2e 0%, #16162a 100%)';
    let border = '2px solid rgba(255,255,255,0.06)';
    let color = '#888';
    let shadow = 'inset 0 1px 0 rgba(255,255,255,0.03)';
    let fontWeight = '600';
    let animStyle = {};

    if (isHit) {
      bg = `linear-gradient(145deg, rgba(245,158,11,0.35) 0%, rgba(200,120,0,0.25) 100%)`;
      border = `2px solid ${ACCENT}`;
      color = '#fff';
      shadow = `0 0 16px rgba(245,158,11,0.4), inset 0 1px 0 rgba(255,255,255,0.1)`;
      fontWeight = '800';
      animStyle = { animation: 'kenoHitGlow 1.5s ease-in-out infinite' };
    } else if (isMiss) {
      bg = `linear-gradient(145deg, rgba(100,100,120,0.2) 0%, rgba(60,60,80,0.15) 100%)`;
      border = '2px solid rgba(255,255,255,0.1)';
      color = '#555';
      shadow = 'none';
      animStyle = { animation: 'kenoTileReveal 0.3s ease-out' };
    } else if (isSelected) {
      bg = `linear-gradient(145deg, ${ACCENT_DIM} 0%, rgba(200,120,0,0.08) 100%)`;
      border = `2px solid ${ACCENT_BORDER}`;
      color = ACCENT;
      shadow = `0 0 10px rgba(245,158,11,0.15)`;
      fontWeight = '800';
    }

    return (
      <div
        key={index}
        className="keno-tile"
        onClick={() => canClick && handleTileSelect(index)}
        style={{
          width: '100%',
          aspectRatio: '1',
          borderRadius: '8px',
          background: bg,
          border,
          boxShadow: shadow,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: isMobile ? '12px' : '14px',
          fontWeight,
          color,
          cursor: canClick ? 'pointer' : 'default',
          transition: canClick ? 'transform 0.1s, box-shadow 0.1s, background 0.15s' : 'none',
          userSelect: 'none',
          position: 'relative',
          ...animStyle,
        }}
        onMouseEnter={e => {
          if (!canClick) return;
          e.currentTarget.style.transform = 'scale(1.08)';
          e.currentTarget.style.boxShadow = `0 0 12px rgba(245,158,11,0.2), inset 0 1px 0 rgba(255,255,255,0.06)`;
        }}
        onMouseLeave={e => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = shadow;
        }}
      >
        {num}
      </div>
    );
  };

  // ─── Payout preview for current config ────────────────────────────────────
  const currentPayouts = KENO_PAYOUTS[riskLevel]?.[numPicks] || {};

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div style={{
      position: isMobile ? 'relative' : 'fixed',
      top: isMobile ? 0 : 0,
      left: isMobile ? undefined : 'var(--sidebar-w)',
      right: isMobile ? undefined : 0,
      bottom: isMobile ? undefined : 0,
      display: 'flex', flexDirection: 'column',
      background: 'var(--bg-primary)',
      overflow: isMobile ? 'visible' : 'auto',
      minHeight: isMobile ? 'calc(100vh - 48px - 60px)' : undefined,
      paddingBottom: isMobile ? '20px' : 0,
    }}>
      {/* Keyframe animations */}
      <style>{`
        @keyframes kenoTileReveal {
          0% { transform: scale(0.5); opacity: 0; }
          50% { transform: scale(1.15); }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes kenoHitGlow {
          0%, 100% { box-shadow: 0 0 10px rgba(245,158,11,0.3); }
          50% { box-shadow: 0 0 25px rgba(245,158,11,0.6), 0 0 50px rgba(245,158,11,0.15); }
        }
        @keyframes kenoResultPop {
          0% { transform: scale(0.8); opacity: 0; }
          60% { transform: scale(1.05); }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes kenoPulse {
          0%, 100% { box-shadow: 0 0 10px rgba(245,158,11,0.3); }
          50% { box-shadow: 0 0 25px rgba(245,158,11,0.5), 0 0 50px rgba(245,158,11,0.15); }
        }
      `}</style>

      {/* Paytable Modal — with independent risk & picks selectors */}
      {showPaytable && <KenoPaytableModal
        isMobile={isMobile}
        defaultRisk={riskLevel}
        defaultPicks={numPicks}
        onClose={() => setShowPaytable(false)}
      />}

      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '12px',
        padding: isMobile ? '12px 12px 8px' : '16px 24px 12px',
        flexShrink: 0,
      }}>
        <button onClick={onBack} style={{
          background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: '8px', color: '#aaa', cursor: 'pointer',
          padding: '6px 12px', fontSize: '13px',
        }}>← Back</button>
        <h2 style={{
          flex: 1, margin: 0, fontSize: isMobile ? '18px' : '22px',
          fontWeight: '800', letterSpacing: '2px', textTransform: 'uppercase',
          color: ACCENT,
        }}>🎱 Keno</h2>
        <div style={{ color: '#ccc', fontSize: '12px', marginRight: '8px' }}>
          {tokenType === 'crewmate' ? '🔵' : '🔴'} {fmtTokens(balance)}
        </div>
        <button onClick={() => setShowPaytable(true)} style={{
          background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: '8px', color: '#aaa', cursor: 'pointer',
          padding: '6px 10px', fontSize: '14px', fontWeight: '700',
        }} title="Paytable">ℹ</button>
        <button onClick={() => setMuted(m => !m)} style={{
          background: 'none', border: 'none', cursor: 'pointer',
          fontSize: '18px', padding: '4px',
        }}>
          {muted ? '🔇' : '🔊'}
        </button>
      </div>

      {/* Main area: grid + controls */}
      <div style={{
        flex: 1, display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? '16px' : '24px',
        padding: isMobile ? '8px 12px' : '8px 24px 24px',
        alignItems: isMobile ? 'center' : 'flex-start',
        justifyContent: 'center',
      }}>
        {/* Grid + Payouts column */}
        <div style={{
          display: 'flex', flexDirection: 'column', gap: isMobile ? '12px' : '14px',
          width: isMobile ? 'calc(100vw - 40px)' : '440px',
          maxWidth: '440px',
          flexShrink: 0,
        }}>
          {/* 8×5 Grid */}
          <div className="keno-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(8, 1fr)',
            gap: isMobile ? '4px' : '6px',
            width: '100%',
          }}>
            {Array.from({ length: BOARD_SIZE }, (_, i) => renderTile(i))}
          </div>

          {/* Payout multiplier row — Stake-style */}
          {(() => {
            const currentHits = drawnTiles ? drawnTiles.slice(0, revealedCount).filter(t => selectedTiles.has(t)).length : 0;
            const isRevealing = gamePhase === 'revealing' || gamePhase === 'result';
            return (
              <>
                {/* Multiplier chips */}
                <div style={{ display: 'flex', gap: '3px' }}>
                  {Array.from({ length: numPicks + 1 }, (_, h) => {
                    const mult = currentPayouts[h] ?? 0;
                    const isActive = isRevealing && currentHits === h && gamePhase === 'result';
                    const isPassed = isRevealing && h < currentHits;
                    const fmtMult = mult >= 1000 ? (mult / 1000).toFixed(0) + 'k' : mult >= 100 ? mult.toFixed(0) : mult.toFixed(2);
                    return (
                      <div key={h} style={{
                        flex: 1,
                        textAlign: 'center',
                        padding: isMobile ? '6px 2px' : '8px 4px',
                        borderRadius: '6px',
                        fontSize: isMobile ? '10px' : '11px',
                        fontWeight: isActive ? '800' : '600',
                        background: isActive ? 'rgba(245,158,11,0.25)' : 'rgba(255,255,255,0.03)',
                        border: `1px solid ${isActive ? ACCENT : 'rgba(255,255,255,0.06)'}`,
                        color: isActive ? '#fff' : isPassed ? '#555' : mult > 0 ? '#aaa' : '#444',
                        boxShadow: isActive ? `0 0 12px rgba(245,158,11,0.3)` : 'none',
                        transition: 'all 0.3s',
                      }}>
                        {mult > 0 ? `${fmtMult}×` : '0×'}
                      </div>
                    );
                  })}
                </div>

                {/* Hit count indicator row */}
                <div style={{ display: 'flex', gap: '3px' }}>
                  {Array.from({ length: numPicks + 1 }, (_, h) => {
                    const isActive = isRevealing && currentHits === h && gamePhase === 'result';
                    const isHit = isRevealing && h <= currentHits && h > 0;
                    return (
                      <div key={h} style={{
                        flex: 1,
                        textAlign: 'center',
                        padding: isMobile ? '4px 2px' : '5px 4px',
                        borderRadius: '6px',
                        fontSize: isMobile ? '10px' : '11px',
                        fontWeight: isActive ? '800' : '600',
                        background: isHit ? 'rgba(0,230,118,0.15)' : isActive ? 'rgba(245,158,11,0.15)' : 'rgba(255,255,255,0.03)',
                        border: `1px solid ${isHit ? 'rgba(0,230,118,0.4)' : isActive ? ACCENT_BORDER : 'rgba(255,255,255,0.06)'}`,
                        color: isHit ? '#00E676' : isActive ? ACCENT : '#555',
                        transition: 'all 0.3s',
                      }}>
                        {h}×
                      </div>
                    );
                  })}
                </div>
              </>
            );
          })()}
        </div>

        {/* Controls panel */}
        <div className="keno-controls" style={{
          width: isMobile ? '100%' : '280px',
          maxWidth: isMobile ? '440px' : '280px',
          display: 'flex', flexDirection: 'column', gap: '14px',
          background: 'rgba(255,255,255,0.02)',
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: '16px',
          padding: isMobile ? '16px' : '20px',
        }}>
          {/* Risk level selector */}
          <div>
            <div style={{ fontSize: '11px', color: '#888', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px', fontWeight: '600' }}>
              Risk
            </div>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {RISK_LEVELS.map(r => (
                <button
                  key={r}
                  onClick={() => { if (!isLocked) { setRiskLevel(r); audioRef.current.init(); audioRef.current.click(); } }}
                  disabled={isLocked}
                  style={{
                    padding: '6px 10px', borderRadius: '8px', cursor: isLocked ? 'not-allowed' : 'pointer',
                    fontSize: '12px', fontWeight: '700',
                    background: riskLevel === r ? ACCENT_DIM : 'rgba(255,255,255,0.04)',
                    border: `1.5px solid ${riskLevel === r ? ACCENT_BORDER : 'rgba(255,255,255,0.08)'}`,
                    color: riskLevel === r ? ACCENT : '#aaa',
                    opacity: isLocked ? 0.5 : 1,
                    transition: 'all 0.15s',
                  }}
                >
                  {RISK_LABELS[r]}
                </button>
              ))}
            </div>
          </div>

          {/* Pick count selector */}
          <div>
            <div style={{ fontSize: '11px', color: '#888', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px', fontWeight: '600' }}>
              Picks
            </div>
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
              {PICK_PRESETS.map(n => (
                <button
                  key={n}
                  onClick={() => { if (!isLocked) { setNumPicks(n); audioRef.current.init(); audioRef.current.click(); } }}
                  disabled={isLocked}
                  style={{
                    padding: '6px 10px', borderRadius: '8px', cursor: isLocked ? 'not-allowed' : 'pointer',
                    fontSize: '12px', fontWeight: '700', minWidth: '32px',
                    background: numPicks === n ? ACCENT_DIM : 'rgba(255,255,255,0.04)',
                    border: `1.5px solid ${numPicks === n ? ACCENT_BORDER : 'rgba(255,255,255,0.08)'}`,
                    color: numPicks === n ? ACCENT : '#aaa',
                    opacity: isLocked ? 0.5 : 1,
                    transition: 'all 0.15s',
                  }}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>

          {/* Bet amount */}
          <div>
            <div style={{ fontSize: '11px', color: '#888', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px', fontWeight: '600' }}>
              Bet Amount
            </div>
            <input
              type="number"
              min={0.1}
              step={0.1}
              value={betAmount}
              disabled={isLocked}
              onChange={e => setBetAmount(e.target.value)}
              style={{
                width: '100%', padding: '10px 12px', borderRadius: '8px',
                background: 'rgba(255,255,255,0.04)', border: '1.5px solid rgba(255,255,255,0.1)',
                color: '#ddd', fontSize: '16px', fontWeight: '600', outline: 'none',
                boxSizing: 'border-box', opacity: isLocked ? 0.5 : 1,
              }}
            />
            <div style={{ display: 'flex', gap: '6px', marginTop: '8px', flexWrap: 'wrap' }}>
              {[
                { label: '½', fn: () => setBetAmount(v => String(Math.max(0.1, parseFloat(v) / 2 || 0.1))) },
                { label: '2x', fn: () => setBetAmount(v => String(Math.min(10000, (parseFloat(v) || 1) * 2))) },
                { label: 'Max', fn: () => setBetAmount(String(Math.min(10000, balance))) },
              ].map(b => (
                <button
                  key={b.label}
                  onClick={() => { if (!isLocked) b.fn(); }}
                  disabled={isLocked}
                  style={{
                    padding: '5px 10px', borderRadius: '6px', cursor: isLocked ? 'not-allowed' : 'pointer',
                    fontSize: '11px', fontWeight: '700',
                    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
                    color: '#aaa', opacity: isLocked ? 0.5 : 1,
                  }}
                >
                  {b.label}
                </button>
              ))}
            </div>
          </div>

          {/* Token type toggle */}
          <div>
            <div style={{ fontSize: '11px', color: '#888', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px', fontWeight: '600' }}>
              Token
            </div>
            <div style={{ display: 'flex', gap: '6px' }}>
              {['crewmate', 'imposter'].map(t => (
                <button
                  key={t}
                  onClick={() => { if (!isLocked) setTokenType(t); }}
                  disabled={isLocked}
                  style={{
                    flex: 1, padding: '8px', borderRadius: '8px',
                    cursor: isLocked ? 'not-allowed' : 'pointer',
                    fontSize: '12px', fontWeight: '700',
                    background: tokenType === t ? (t === 'crewmate' ? 'rgba(58,143,212,0.15)' : 'rgba(232,56,74,0.15)') : 'rgba(255,255,255,0.04)',
                    border: `1.5px solid ${tokenType === t ? (t === 'crewmate' ? 'rgba(58,143,212,0.5)' : 'rgba(232,56,74,0.5)') : 'rgba(255,255,255,0.08)'}`,
                    color: tokenType === t ? (t === 'crewmate' ? '#3A8FD4' : '#E8384A') : '#777',
                    opacity: isLocked ? 0.5 : 1,
                  }}
                >
                  {t === 'crewmate' ? '🔵 Crewmate' : '🔴 Imposter'}
                </button>
              ))}
            </div>
          </div>

          {/* Reveal progress */}
          {(gamePhase === 'revealing' || gamePhase === 'playing') && (
            <div style={{
              textAlign: 'center', padding: '12px',
              background: 'rgba(0,0,0,0.2)', borderRadius: '10px',
              border: '1px solid rgba(255,255,255,0.05)',
            }}>
              <div style={{ fontSize: '11px', color: '#888', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>
                Drawing...
              </div>
              <div style={{ fontSize: '22px', fontWeight: '800', color: ACCENT }}>
                {revealedCount} / {DRAW_COUNT}
              </div>
              {hits.length > 0 && (
                <div style={{ fontSize: '12px', color: '#aaa', marginTop: '4px' }}>
                  Hits: {drawnTiles ? drawnTiles.slice(0, revealedCount).filter(t => selectedTiles.has(t)).length : 0} / {numPicks}
                </div>
              )}
            </div>
          )}

          {/* Result card */}
          {gamePhase === 'result' && resultData && (
            <div style={{
              textAlign: 'center', padding: '16px',
              background: resultData.payout > 0 ? 'rgba(0,230,118,0.1)' : 'rgba(232,56,74,0.1)',
              borderRadius: '10px',
              border: `1px solid ${resultData.payout > 0 ? 'rgba(0,230,118,0.3)' : 'rgba(232,56,74,0.3)'}`,
              animation: 'kenoResultPop 0.3s ease-out',
            }}>
              {resultData.payout > 0 ? (
                <>
                  <div style={{ fontSize: '24px', marginBottom: '4px' }}>🎉</div>
                  <div style={{ fontSize: '16px', fontWeight: '700', color: '#00E676' }}>
                    {hits.length} Hit{hits.length !== 1 ? 's' : ''}!
                  </div>
                  <div style={{ fontSize: '22px', fontWeight: '800', color: ACCENT, marginTop: '4px' }}>
                    {tokenType === 'crewmate' ? '🔵' : '🔴'} {fmtTokens(resultData.payout)}
                  </div>
                  <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>
                    at {resultData.multiplier.toLocaleString()}x
                  </div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: '24px', marginBottom: '4px' }}>💨</div>
                  <div style={{ fontSize: '16px', fontWeight: '700', color: '#E8384A' }}>No Luck</div>
                  <div style={{ fontSize: '13px', color: '#aaa', marginTop: '4px' }}>
                    {drawnTiles ? drawnTiles.filter(t => selectedTiles.has(t)).length : 0} hit{drawnTiles && drawnTiles.filter(t => selectedTiles.has(t)).length !== 1 ? 's' : ''} — Lost {tokenType === 'crewmate' ? '🔵' : '🔴'} {fmtTokens(parseFloat(betAmount))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Action buttons */}
          {gamePhase === 'picking' && (
            <>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={handleAutoPick}
                  style={{
                    flex: 1, padding: '10px', borderRadius: '8px', cursor: 'pointer',
                    fontSize: '12px', fontWeight: '700',
                    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)',
                    color: '#aaa',
                  }}
                >
                  🎲 Auto Pick
                </button>
                <button
                  onClick={handleClearPicks}
                  disabled={selectedTiles.size === 0}
                  style={{
                    flex: 1, padding: '10px', borderRadius: '8px',
                    cursor: selectedTiles.size === 0 ? 'not-allowed' : 'pointer',
                    fontSize: '12px', fontWeight: '700',
                    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)',
                    color: '#aaa', opacity: selectedTiles.size === 0 ? 0.5 : 1,
                  }}
                >
                  ✕ Clear
                </button>
              </div>
              <button
                onClick={handlePlay}
                disabled={loading || selectedTiles.size !== numPicks}
                style={{
                  width: '100%', padding: '14px', borderRadius: '10px',
                  cursor: (loading || selectedTiles.size !== numPicks) ? 'not-allowed' : 'pointer',
                  fontSize: '14px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '1.5px',
                  background: (loading || selectedTiles.size !== numPicks) ? '#333' : `linear-gradient(135deg, ${ACCENT}, #D97706)`,
                  border: 'none', color: '#fff',
                  boxShadow: (loading || selectedTiles.size !== numPicks) ? 'none' : `0 4px 20px rgba(245,158,11,0.3)`,
                  opacity: (loading || selectedTiles.size !== numPicks) ? 0.6 : 1,
                  animation: selectedTiles.size === numPicks && !loading ? 'kenoPulse 2s ease-in-out infinite' : 'none',
                  transition: 'all 0.2s',
                }}
              >
                {loading ? 'Starting...' : selectedTiles.size === numPicks ? 'Play' : `Pick ${numPicks - selectedTiles.size} more`}
              </button>
            </>
          )}

          {(gamePhase === 'playing' || gamePhase === 'revealing') && (
            <button disabled style={{
              width: '100%', padding: '14px', borderRadius: '10px',
              cursor: 'not-allowed', fontSize: '14px', fontWeight: '800',
              textTransform: 'uppercase', letterSpacing: '1.5px',
              background: '#333', border: 'none', color: '#888',
              opacity: 0.6,
            }}>
              Drawing...
            </button>
          )}

          {gamePhase === 'result' && (
            <button
              onClick={handleNewGame}
              style={{
                width: '100%', padding: '14px', borderRadius: '10px',
                cursor: 'pointer',
                fontSize: '14px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '1.5px',
                background: `linear-gradient(135deg, ${ACCENT}40, ${ACCENT}15)`,
                border: `1.5px solid ${ACCENT_BORDER}`,
                color: ACCENT,
                transition: 'all 0.2s',
              }}
            >
              New Game
            </button>
          )}

          {/* Selection counter */}
          {gamePhase === 'picking' && (
            <div style={{ textAlign: 'center', fontSize: '11px', color: '#555' }}>
              {selectedTiles.size} / {numPicks} tiles selected
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default KenoPage;
